import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { Loader2Icon } from 'lucide-react';
import { canvasSize, PageBox, PREVIEW_PIXEL_RATIO, PT_TO_PX } from './geometry';
import { releaseCanvas, RenderCache } from './render-cache';

type PdfViewerPageProps = {
	doc: PDFDocumentProxy;
	box: PageBox;
	pageCount: number;
	/** Inside the virtualization window: hold a rendered bitmap. */
	isNear: boolean;
	/** Device px per base px to render at (see canvasPixelRatio). */
	pixelRatio: number;
	cache: RenderCache<HTMLCanvasElement>;
	overlay?: React.ReactNode;
	registerElement: (index: number, element: HTMLElement | null) => void;
	onRenderError?: (error: unknown) => void;
};

const isCancellation = (error: unknown) =>
	(error as { name?: string } | null)?.name === 'RenderingCancelledException';

/**
 * One page block. Bitmaps are always laid out at base size (CSS px at 96 dpi); only
 * their backing store follows `pixelRatio`, so a zoom change first scales the current
 * bitmap through the parent transform and then swaps in a sharp one when it is ready.
 * The rendered canvas itself is what gets displayed and cached, so a page costs one
 * bitmap, not a cached copy plus an on-screen copy.
 */
export const PdfViewerPage = memo(function PdfViewerPage(
	props: PdfViewerPageProps,
) {
	const {
		doc,
		box,
		isNear,
		pixelRatio,
		cache,
		onRenderError,
		registerElement,
	} = props;
	const hostRef = useRef<HTMLDivElement>(null);
	const shownRatioRef = useRef<number | null>(null);
	const shownCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const [hasBitmap, setHasBitmap] = useState(false);

	const elementRef = useCallback(
		(element: HTMLDivElement | null) => registerElement(box.index, element),
		[registerElement, box.index],
	);

	useEffect(() => {
		const host = hostRef.current;
		if (!isNear || !host) {
			// The host unmounted with the page leaving the window: free its bitmap now
			// unless the cache keeps it for scrolling back (iOS only reclaims canvas
			// memory late otherwise).
			const shown = shownCanvasRef.current;
			if (shown && !cache.holds(shown)) releaseCanvas(shown);
			shownCanvasRef.current = null;
			shownRatioRef.current = null;
			setHasBitmap(false);
			return;
		}

		const show = (canvas: HTMLCanvasElement, ratio: number) => {
			canvas.style.width = '100%';
			canvas.style.height = '100%';
			canvas.style.display = 'block';
			if (host.firstChild !== canvas) host.replaceChildren(canvas);
			const previous = shownCanvasRef.current;
			if (previous && previous !== canvas && !cache.holds(previous)) {
				releaseCanvas(previous);
			}
			shownCanvasRef.current = canvas;
			shownRatioRef.current = ratio;
			setHasBitmap(true);
		};

		let isCancelled = false;
		let task: RenderTask | null = null;
		const render = async (ratio: number) => {
			const key = RenderCache.key(box.index, ratio);
			const cached = cache.get(key);
			if (cached) return cached;
			const page = await doc.getPage(box.index + 1);
			if (isCancelled) return null;
			const viewport = page.getViewport({ scale: PT_TO_PX * ratio });
			const canvas = document.createElement('canvas');
			// Same rounding canvasPixelRatio budgeted for.
			const size = canvasSize(box, ratio);
			canvas.width = size.width;
			canvas.height = size.height;
			const context = canvas.getContext('2d');
			if (!context) return null;
			task = page.render({ canvas, canvasContext: context, viewport });
			await task.promise;
			task = null;
			cache.set(key, canvas);
			return canvas;
		};

		(async () => {
			if (shownRatioRef.current === pixelRatio) return;
			// Nothing on screen yet and no sharp bitmap cached: show a cheap low-res
			// pass first so the page is never blank while the sharp one renders.
			if (
				shownRatioRef.current === null &&
				pixelRatio > PREVIEW_PIXEL_RATIO &&
				!cache.get(RenderCache.key(box.index, pixelRatio))
			) {
				const preview = await render(PREVIEW_PIXEL_RATIO);
				if (isCancelled) return;
				if (preview) show(preview, PREVIEW_PIXEL_RATIO);
			}
			const sharp = await render(pixelRatio);
			if (!isCancelled && sharp) show(sharp, pixelRatio);
		})().catch((error) => {
			if (isCancelled || isCancellation(error)) return;
			onRenderError?.(error);
		});

		return () => {
			isCancelled = true;
			task?.cancel();
		};
	}, [doc, box.index, isNear, pixelRatio, cache, onRenderError]);

	return (
		<div
			ref={elementRef}
			data-page-index={box.index}
			className="absolute"
			style={{ left: box.left, top: box.top, width: box.width }}
		>
			<div
				className="relative bg-white shadow-[0_0_0_1px_#ccc]"
				style={{ width: box.width, height: box.height }}
			>
				{isNear && (
					<div
						ref={hostRef}
						className="absolute inset-0"
						aria-hidden
					/>
				)}
				{!hasBitmap && (
					<div className="absolute inset-0 flex items-center justify-center text-[#999]">
						<Loader2Icon className="animate-spin" />
					</div>
				)}
				<div className="absolute inset-0" data-page-overlay={box.index}>
					{props.overlay}
				</div>
			</div>
			<div className="flex h-7 items-center justify-center text-xs text-[#666] select-none">
				{box.index + 1} de {props.pageCount}
			</div>
		</div>
	);
});
